/**
 * +presence: shared-field presence wire protocol (docs/design/multiplayer.md §10, zones.md "Shared
 * fields"), shared by the browser (net/fieldPresence.ts) and the FieldShard Durable Object
 * (apps/server/src/field.ts, fieldCore.ts). JSON over one WebSocket per player per field.
 *
 * v1 is presence only: every client runs its own field sim (its own mobs, loot and XP); the shard
 * relays where the other heroes are and what they do (run, cast, jump, level-up, emote, chat).
 *
 * Pure: no DOM, no clock.
 */
import { WAYSTONE_COOLDOWN_MS, WAYSTONE_USE_YD } from '@keystone/sim';
import { cleanChatText } from './protocol';

/** Players per shard; the next shard opens when this one is full. */
export const FIELD_CAP = 30;
/** A party member may join a full shard of their party up to FIELD_CAP + this. */
export const FIELD_PARTY_SLACK = 4;
/** Shards per field (zone:field:0 … :N-1). */
export const FIELD_MAX_SHARDS = 64;
/** Client state rate while something changes (Hz); unchanged state is re-sent every FIELD_KEEPALIVE_MS. */
export const FIELD_SEND_HZ = 10;
export const FIELD_KEEPALIVE_MS = 5000;
/** Shard broadcast period (ms): one batched snapshot of every changed player. */
export const FIELD_BROADCAST_MS = 100;
/**
 * Interest range (yd): a client gets other players' movement and their cast / jump / emote events
 * only while they are this close (a zone map can be several hundred yards across). Joins, leaves,
 * level, AFK and zone chat go to the whole shard.
 */
export const FIELD_INTEREST_RANGE = 90;
/** Unfocused this long: shown with an AFK tag. Hidden this long: disconnected. Silent this long: dropped. */
export const FIELD_AFK_MS = 2 * 60_000;
export const FIELD_HIDDEN_KICK_MS = 15 * 60_000;
export const FIELD_SILENT_KICK_MS = 3 * 60_000;
/** Movement budget refill (yd/s) and burst (yd): runs, dashes and jitter pass; teleports converge. */
export const FIELD_MAX_SPEED = 14;
export const FIELD_MOVE_BURST = 22;
/** Bounds slack (yd) around the field's Rect. */
export const FIELD_BOUNDS_SLACK = 2;
/** A cast's VFX point may be at most this far from the caster (yd). */
export const FIELD_MAX_FX_RANGE = 50;
/**
 * +presence: waystone travel (`{ k: 'travel' }`). The claimed arrival must lie within
 * FIELD_TRAVEL_ARRIVE_YD of the named stone (the sim sets heroes down 2.5 yd from it), the last
 * accepted position within FIELD_TRAVEL_FROM_YD of some stone of the field (where the channel ran;
 * the use range plus slack), and one hero may travel at most once per FIELD_TRAVEL_COOLDOWN_MS (the
 * sim's cooldown, less clock jitter between the client's sim and the shard).
 */
export const FIELD_TRAVEL_ARRIVE_YD = 6;
export const FIELD_TRAVEL_FROM_YD = WAYSTONE_USE_YD + 6;
export const FIELD_TRAVEL_COOLDOWN_MS = WAYSTONE_COOLDOWN_MS - 3000;
/** A waystone of the field as the shard knows it (from the content, never the client). */
export interface FieldWaystone {
  id: string;
  x: number;
  y: number;
}
/** Rate caps (token buckets: refill per second, burst). */
export const FIELD_RATE = {
  msgs: { per: 30, burst: 60 },
  events: { per: 8, burst: 16 },
  chat: { per: 0.7, burst: 4 },
} as const;
/** More than this many messages in one second closes the socket (abuse, not jitter). */
export const FIELD_FLOOD = 200;
/** Emotes a client may trigger (the hero clip each plays: render/toon/anim/emotes.ts). */
export const FIELD_EMOTES = ['cheer'] as const;
export type FieldEmote = (typeof FIELD_EMOTES)[number];

/** Zone / field ids in the URL (the shard checks them against the content). */
export const FIELD_ID_RE = /^[a-z0-9_]{1,40}$/;

/** Close codes the client must not reconnect on right away. */
export const FIELD_CLOSE = { replaced: 4000, refused: 4001, idle: 4002, flood: 4008 } as const;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** Animation bits (`an`). */
export const AN_CAST = 1;
export const AN_AIR = 2;
export const AN_DEAD = 4;
export const AN_CHANNEL = 8;
/** +glide: the glider is open (the canopy and the hanging pose). */
export const AN_GLIDE = 16;
/** +glide: heights (yd) a hero's `z` may claim, and how fast it may change (yd/s: the fastest fall plus an updraft's lift, with burst). */
export const FIELD_Z_MIN = -20;
export const FIELD_Z_MAX = 200;
export const FIELD_MAX_VSPEED = 34;
export const FIELD_Z_BURST = 12;

/** One hero's state as sent by its client (~10 Hz while it changes). */
export interface FieldState {
  x: number;
  y: number;
  /** Facing (radians). */
  f: number;
  /** Ground velocity (yd/s). */
  vx: number;
  vy: number;
  /** AN_* bits. */
  an: number;
  /** Casting / channelling ability id (with AN_CAST / AN_CHANNEL). */
  c?: string;
  /** Current target's broad kind: 'e' enemy, 'f' friendly. */
  tk?: 'e' | 'f';
  /** Tab state: 0 focused, 1 visible but unfocused, 2 hidden. */
  vis?: 0 | 1 | 2;
  /** +glide: absolute height (yd) while in free flight or on a platform (absent = on the ground). */
  z?: number;
}

/** Discrete events a client reports. */
export type FieldEventIn =
  /** Cast start: ability, cast / channel time (ms). */
  | { k: 'cs'; a: string; d: number }
  /** Cast finished (instants too): ability and the VFX point (target position or ground point). */
  | { k: 'cf'; a: string; tp?: [number, number] }
  /** Cast cancelled / interrupted. */
  | { k: 'cx' }
  | { k: 'jump' }
  /** "I levelled up": the shard re-reads the level from the profile (never trusts a number). */
  | { k: 'lvl' }
  | { k: 'emote'; a: FieldEmote }
  /**
   * +presence: "I just travelled by waystone `w` and stand at (x, y)". Sent on the sim's waystone
   * event, BEFORE the state from the new place: the shard validates it against the content and
   * snaps the hero there instead of running it through the movement budget.
   */
  | { k: 'travel'; w: string; x: number; y: number };

export type FieldClientMsg =
  | { t: 'hello'; build: string }
  | ({ t: 's' } & FieldState)
  | ({ t: 'e' } & FieldEventIn)
  | { t: 'chat'; text: string }
  | { t: 'ping'; n: number };

/** A player as the shard shows it (identity from the server profile, never from the client). */
export interface FieldPlayerInfo {
  /** Per-shard id (never the account id). */
  id: number;
  name: string;
  cls: string;
  lvl: number;
  look: number;
  afk: boolean;
  /** Last known state (null until the first one). */
  s: FieldState | null;
}

/** Snapshot row: [id, x, y, f, vx, vy, an, c, tk, z?] (+glide: z = absolute height, absent / null on the ground). */
export type FieldRow = [number, number, number, number, number, number, number, string, string] | [number, number, number, number, number, number, number, string, string, number | null];

/** An event as broadcast (`id` = who). */
export type FieldEventOut =
  | { id: number; k: 'cs'; a: string; d: number }
  | { id: number; k: 'cf'; a: string; tp?: [number, number] }
  | { id: number; k: 'cx' }
  | { id: number; k: 'jump' }
  | { id: number; k: 'lvl'; lvl: number }
  | { id: number; k: 'emote'; a: FieldEmote }
  /** +presence: a validated waystone jump: snap the avatar from `from` to `to` (a poof at both). */
  | { id: number; k: 'travel'; w: string; from: [number, number]; to: [number, number] }
  | { id: number; k: 'chat'; text: string }
  | { id: number; k: 'afk'; on: boolean };

export type FieldServerMsg =
  | { t: 'welcome'; you: number; shard: number; key: string; players: FieldPlayerInfo[] }
  | { t: 'join'; p: FieldPlayerInfo }
  | { t: 'leave'; id: number }
  /** `ts`: server time of the batch (ms); rows of the players that changed; their events in order; `o`: players now out of interest range (hide them). */
  | { t: 'snap'; ts: number; s: FieldRow[]; ev?: FieldEventOut[]; o?: number[] }
  | { t: 'error'; code: string; msg: string }
  | { t: 'pong'; n: number };

// ---------------------------------------------------------------------------
// Sanitising (server side; the client uses the same shapes)
// ---------------------------------------------------------------------------

export interface Rect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const ABILITY_RE = /^[A-Za-z0-9_.:-]{1,48}$/;
const abilityId = (v: unknown): string | null => (typeof v === 'string' && ABILITY_RE.test(v) ? v : null);
const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const r2 = (v: number): number => Math.round(v * 100) / 100;

/** Wrap an angle into (-π, π]. */
export function wrapAngle(a: number): number {
  const t = Math.PI * 2;
  let x = a % t;
  if (x > Math.PI) x -= t;
  else if (x <= -Math.PI) x += t;
  return x;
}

export function clampToRect(x: number, y: number, b: Rect | null, slack = FIELD_BOUNDS_SLACK): [number, number] {
  if (!b) return [x, y];
  return [clamp(x, b.minX - slack, b.maxX + slack), clamp(y, b.minY - slack, b.maxY + slack)];
}

/** A client's state rebuilt field by field (positions clamped to `bounds`), or null when malformed. */
export function sanitizeFieldState(raw: unknown, bounds: Rect | null): FieldState | null {
  const o = raw as Record<string, unknown> | null;
  if (!o || typeof o !== 'object') return null;
  if (!finite(o['x']) || !finite(o['y'])) return null;
  const [x, y] = clampToRect(o['x'], o['y'], bounds);
  let vx = finite(o['vx']) ? o['vx'] : 0;
  let vy = finite(o['vy']) ? o['vy'] : 0;
  const sp = Math.hypot(vx, vy);
  if (sp > FIELD_MAX_SPEED) {
    vx = (vx / sp) * FIELD_MAX_SPEED;
    vy = (vy / sp) * FIELD_MAX_SPEED;
  }
  const an = finite(o['an']) ? (o['an'] | 0) & (AN_CAST | AN_AIR | AN_DEAD | AN_CHANNEL | AN_GLIDE) : 0;
  const out: FieldState = { x: r2(x), y: r2(y), f: r2(finite(o['f']) ? wrapAngle(o['f']) : 0), vx: r2(vx), vy: r2(vy), an };
  if (finite(o['z'])) out.z = r2(clamp(o['z'], FIELD_Z_MIN, FIELD_Z_MAX)); // +glide
  const c = abilityId(o['c']);
  if (c && an & (AN_CAST | AN_CHANNEL)) out.c = c;
  if (o['tk'] === 'e' || o['tk'] === 'f') out.tk = o['tk'];
  if (o['vis'] === 1 || o['vis'] === 2) out.vis = o['vis'];
  return out;
}

/** A client event, rebuilt (VFX points clamped to bounds and near the caster), or null. */
export function sanitizeFieldEvent(raw: unknown, bounds: Rect | null, from: { x: number; y: number } | null): FieldEventIn | null {
  const o = raw as Record<string, unknown> | null;
  if (!o || typeof o !== 'object') return null;
  switch (o['k']) {
    case 'cs': {
      const a = abilityId(o['a']);
      return a ? { k: 'cs', a, d: finite(o['d']) ? Math.round(clamp(o['d'], 0, 10_000)) : 0 } : null;
    }
    case 'cf': {
      const a = abilityId(o['a']);
      if (!a) return null;
      const tp = o['tp'] as unknown[] | undefined;
      if (Array.isArray(tp) && finite(tp[0]) && finite(tp[1])) {
        let [x, y] = clampToRect(tp[0], tp[1], bounds);
        if (from) {
          const d = Math.hypot(x - from.x, y - from.y);
          if (d > FIELD_MAX_FX_RANGE) {
            x = from.x + ((x - from.x) / d) * FIELD_MAX_FX_RANGE;
            y = from.y + ((y - from.y) / d) * FIELD_MAX_FX_RANGE;
          }
        }
        return { k: 'cf', a, tp: [r2(x), r2(y)] };
      }
      return { k: 'cf', a };
    }
    case 'cx':
      return { k: 'cx' };
    case 'jump':
      return { k: 'jump' };
    case 'lvl':
      return { k: 'lvl' };
    case 'emote':
      return (FIELD_EMOTES as readonly unknown[]).includes(o['a']) ? { k: 'emote', a: o['a'] as FieldEmote } : null;
    case 'travel': {
      // Shape only: the shard checks the stone, the distances and the cooldown (FieldShardCore).
      const w = o['w'];
      if (typeof w !== 'string' || !FIELD_ID_RE.test(w) || !finite(o['x']) || !finite(o['y'])) return null;
      const [x, y] = clampToRect(o['x'], o['y'], bounds);
      return { k: 'travel', w, x: r2(x), y: r2(y) };
    }
    default:
      return null;
  }
}

/** Zone chat text: the party chat's cleaner (control chars, length), or null when empty. */
export function sanitizeFieldChat(raw: unknown): string | null {
  return typeof raw === 'string' ? cleanChatText(raw) : null;
}

/** The shard's name (Durable Object `idFromName`). */
export function fieldShardKey(zoneId: string, fieldId: string, shard: number): string {
  return `${zoneId}:${fieldId}:${shard}`;
}

export function parseFieldShardKey(key: string): { zoneId: string; fieldId: string; shard: number } | null {
  const m = /^([a-z0-9_]{1,40}):([a-z0-9_]{1,40}):(\d{1,3})$/.exec(key);
  return m ? { zoneId: m[1]!, fieldId: m[2]!, shard: Number(m[3]) } : null;
}

/** Token bucket (rate caps). */
export class Bucket {
  private tokens: number;
  private at: number;
  constructor(
    private readonly per: number,
    private readonly burst: number,
    now: number,
  ) {
    this.tokens = burst;
    this.at = now;
  }
  take(now: number, n = 1): boolean {
    this.tokens = Math.min(this.burst, this.tokens + ((now - this.at) / 1000) * this.per);
    this.at = now;
    if (this.tokens < n) return false;
    this.tokens -= n;
    return true;
  }
}
